<template>
  <div class="card border-0 shadow-sm">
    <div class="card-header bg-white border-bottom">
      <h5 class="mb-0">{{ $t('employee.contract.title') }}</h5>
    </div>
    <div class="card-body">
      <!-- Loading -->
      <p v-if="loading" class="text-muted mb-0">{{ $t('common.loading') }}</p>

      <!-- Load error -->
      <div v-else-if="error" class="alert alert-danger mb-0" role="alert">
        {{ error }}
      </div>

      <!-- No contract uploaded by the admin yet -->
      <div v-else-if="!currentContract" class="text-muted mb-0 d-flex align-items-center gap-2">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" class="bi bi-file-earmark-x" viewBox="0 0 16 16">
          <path d="M6.854 7.146a.5.5 0 1 0-.708.708L7.293 9l-1.147 1.146a.5.5 0 0 0 .708.708L8 9.707l1.146 1.147a.5.5 0 0 0 .708-.708L8.707 9l1.147-1.146a.5.5 0 0 0-.708-.708L8 8.293 6.854 7.146z" />
          <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2zM9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5v2z" />
        </svg>
        {{ $t('employee.contract.noContract') }}
      </div>

      <!-- Current contract -->
      <div v-else>
        <div class="d-flex align-items-start gap-3 mb-3">
          <div class="contract-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" class="bi bi-file-earmark-text" viewBox="0 0 16 16">
              <path d="M5.5 7a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5zM5 9.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1h-2a.5.5 0 0 1-.5-.5z" />
              <path d="M9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.5L9.5 0zm0 1v2A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z" />
            </svg>
          </div>
          <div class="flex-grow-1 min-width-0">
            <p class="fw-semibold mb-1 text-break">
              {{ currentContract.filename }}
              <span class="badge bg-success ms-1">{{ $t('employee.contract.current') }}</span>
            </p>
            <small class="text-muted d-block">{{ formatFileSize(currentContract.size) }}</small>
            <small class="text-muted d-block">
              {{ $t('employee.contract.uploadedOn', { date: formatDate(currentContract.uploadedAt) }) }}
            </small>
          </div>
        </div>

        <div class="d-flex flex-wrap gap-2">
          <button class="btn btn-primary btn-sm" @click="viewContract(currentContract)" :disabled="busy">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-eye me-1" viewBox="0 0 16 16">
              <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z" />
              <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z" />
            </svg>
            {{ $t('employee.contract.view') }}
          </button>
          <button class="btn btn-outline-primary btn-sm" @click="downloadContract(currentContract)" :disabled="busy">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-download me-1" viewBox="0 0 16 16">
              <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
              <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z" />
            </svg>
            {{ busy ? $t('common.loading') : $t('employee.contract.download') }}
          </button>
        </div>

        <div v-if="actionError" class="alert alert-danger mt-3 mb-0 py-2 small" role="alert">
          {{ actionError }}
        </div>

        <!-- Previously uploaded contracts -->
        <div v-if="previousContracts.length" class="mt-4 pt-3 border-top">
          <button
            class="btn btn-link btn-sm p-0 text-decoration-none"
            @click="showHistory = !showHistory"
          >
            {{ $t('employee.contract.previousDocuments', { count: previousContracts.length }) }}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              fill="currentColor"
              class="bi ms-1"
              :class="showHistory ? 'bi-chevron-up' : 'bi-chevron-down'"
              viewBox="0 0 16 16"
            >
              <path
                v-if="showHistory"
                fill-rule="evenodd"
                d="M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708l6-6z"
              />
              <path
                v-else
                fill-rule="evenodd"
                d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"
              />
            </svg>
          </button>

          <ul v-if="showHistory" class="list-unstyled mt-3 mb-0">
            <li
              v-for="contract in previousContracts"
              :key="contract.id"
              class="d-flex align-items-center justify-content-between gap-3 py-2 border-bottom"
            >
              <div class="min-width-0">
                <p class="mb-0 text-break small fw-semibold">{{ contract.filename }}</p>
                <small class="text-muted">
                  {{ formatFileSize(contract.size) }} ·
                  {{ $t('employee.contract.uploadedOn', { date: formatDate(contract.uploadedAt) }) }}
                </small>
              </div>
              <div class="d-flex gap-2 flex-shrink-0">
                <button
                  class="btn btn-outline-secondary btn-sm"
                  @click="viewContract(contract)"
                  :disabled="busy"
                >
                  {{ $t('employee.contract.view') }}
                </button>
                <button
                  class="btn btn-outline-secondary btn-sm"
                  @click="downloadContract(contract)"
                  :disabled="busy"
                >
                  {{ $t('employee.contract.download') }}
                </button>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const API_URL = process.env.VUE_APP_API_URL || "http://localhost:4000";

export default {
  name: "ContractDocument",
  data() {
    return {
      contracts: [],
      loading: false,
      busy: false,
      showHistory: false,
      error: "",
      actionError: "",
    };
  },
  computed: {
    // The newest document is the current contract, the rest is history
    currentContract() {
      return this.contracts[0] || null;
    },
    previousContracts() {
      return this.contracts.slice(1);
    },
  },
  mounted() {
    this.loadContracts();
  },
  methods: {
    async loadContracts() {
      this.loading = true;
      this.error = "";
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${API_URL}/api/employee/contracts`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch contracts");
        }
        const result = await response.json();
        this.contracts = result.data || [];
      } catch (error) {
        console.error("Error loading contracts:", error);
        this.error = this.$t("employee.contract.loadError");
      } finally {
        this.loading = false;
      }
    },
    async fetchContractBlob(contractId, inline) {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_URL}/api/employee/contracts/${contractId}/download?inline=${inline}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch contract file");
      }
      return await response.blob();
    },
    async viewContract(contract) {
      this.actionError = "";
      this.busy = true;
      // Opened before the request so the browser does not treat it as a popup
      const newWindow = window.open("", "_blank");
      try {
        const blob = await this.fetchContractBlob(contract.id, true);
        const objectUrl = URL.createObjectURL(blob);
        if (newWindow) {
          newWindow.location.href = objectUrl;
        } else {
          window.open(objectUrl, "_blank");
        }
        // Keep the object alive long enough for the new tab to render it
        setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
      } catch (error) {
        console.error("Error viewing contract:", error);
        if (newWindow) {
          newWindow.close();
        }
        this.actionError = this.$t("employee.contract.viewError");
      } finally {
        this.busy = false;
      }
    },
    async downloadContract(contract) {
      this.actionError = "";
      this.busy = true;
      try {
        const blob = await this.fetchContractBlob(contract.id, false);
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = contract.filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(objectUrl);
      } catch (error) {
        console.error("Error downloading contract:", error);
        this.actionError = this.$t("employee.contract.downloadError");
      } finally {
        this.busy = false;
      }
    },
    formatFileSize(bytes) {
      if (!bytes) return "0 B";
      const units = ["B", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
    },
    formatDate(date) {
      if (!date) return "N/A";
      return new Date(date).toLocaleDateString("de-DE", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
  },
};
</script>

<style scoped>
.contract-icon {
  color: #667eea;
}

.min-width-0 {
  min-width: 0;
}
</style>
